import React, { useState, useEffect, useCallback } from 'react';
import { Button, Badge, Input, Modal, Loader, Alert, Card } from './shared';
import Table, { TableHead, TableBody, TableRow, TableCell } from './shared/Table';

const GitHubTokenManager = ({ service, isOnline }) => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateToken, setShowCreateToken] = useState(false);
  const [showOAuthSetup, setShowOAuthSetup] = useState(false);
  const [vaultToken, setVaultToken] = useState(null);
  const [githubUser, setGithubUser] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // GitHub OAuth Config (you'll need to create a GitHub OAuth App)
  const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || '';
  const GITHUB_REDIRECT_URI = `${window.location.origin}/api/github/callback`;

  // API endpoints
  const VAULT_API = window.location.protocol === 'https:'
    ? '/api/vault'
    : `http://${window.location.hostname}:8000`;

  // New token form
  const [newToken, setNewToken] = useState({
    name: '',
    scopes: ['repo'],
    description: '',
    expirationDays: 90
  });

  // Available GitHub token scopes
  const availableScopes = [
    { value: 'repo', label: 'Full control of private repositories', category: 'repos' },
    { value: 'repo:status', label: 'Access commit status', category: 'repos' },
    { value: 'repo_deployment', label: 'Access deployment status', category: 'repos' },
    { value: 'public_repo', label: 'Access public repositories', category: 'repos' },
    { value: 'read:org', label: 'Read org and team membership', category: 'orgs' },
    { value: 'write:org', label: 'Read and write org membership', category: 'orgs' },
    { value: 'admin:org', label: 'Full control of orgs', category: 'orgs' },
    { value: 'user', label: 'Update all user data', category: 'user' },
    { value: 'read:user', label: 'Read all user profile data', category: 'user' },
    { value: 'user:email', label: 'Access user email addresses', category: 'user' },
  ];

  // Get vault authentication token
  const getVaultToken = useCallback(async () => {
    if (vaultToken) return vaultToken;

    try {
      const response = await fetch(`${VAULT_API}/api/v1/auth/service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service_name: 'admin-dashboard' })
      });

      if (response.ok) {
        const data = await response.json();
        setVaultToken(data.access_token);
        return data.access_token;
      }
    } catch (err) {
      console.error('Failed to get vault token:', err);
    }
    return null;
  }, [VAULT_API, vaultToken]);

  // Load tokens from vault
  const loadTokens = useCallback(async () => {
    if (!isOnline) return;

    setLoading(true);
    setError(null);

    try {
      const token = await getVaultToken();
      if (!token) {
        throw new Error('Failed to authenticate with vault');
      }

      // Get all GitHub-related secrets
      const response = await fetch(`${VAULT_API}/api/v1/secrets/shared/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter for GitHub tokens
        const githubTokens = data.filter(secret =>
          secret.secret_key.startsWith('GITHUB_TOKEN') ||
          secret.secret_key === 'github_token'
        );
        setTokens(githubTokens);
      } else {
        throw new Error(`Failed to load tokens: ${response.status}`);
      }
    } catch (err) {
      console.error('Failed to load tokens:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isOnline, getVaultToken, VAULT_API]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  // Check if user is authenticated with GitHub
  useEffect(() => {
    const checkGitHubAuth = async () => {
      const token = await getVaultToken();
      if (!token) return;

      try {
        const response = await fetch(`${VAULT_API}/api/v1/secrets/shared/GITHUB_TOKEN`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.secret_value && !data.secret_value.includes('PLACEHOLDER')) {
            // Token exists, verify it with GitHub
            await verifyGitHubToken(data.secret_value);
          }
        }
      } catch (err) {
        console.error('Failed to check GitHub auth:', err);
      }
    };

    checkGitHubAuth();
  }, [getVaultToken, VAULT_API]);

  // Verify GitHub token and get user info
  const verifyGitHubToken = async (token) => {
    try {
      const response = await fetch('https://api.github.com/user', {
        headers: {
          'Authorization': `token ${token}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setGithubUser(userData);
      } else {
        setGithubUser(null);
      }
    } catch (err) {
      console.error('Failed to verify GitHub token:', err);
      setGithubUser(null);
    }
  };

  // GitHub OAuth flow
  const startGitHubOAuth = () => {
    if (!GITHUB_CLIENT_ID) {
      setError('GitHub OAuth is not configured. Please set REACT_APP_GITHUB_CLIENT_ID environment variable.');
      setShowOAuthSetup(true);
      return;
    }

    const scope = 'repo,delete_repo,read:org,user:email';
    const authUrl = `https://github.com/login/oauth/authorize?client_id=${GITHUB_CLIENT_ID}&redirect_uri=${encodeURIComponent(GITHUB_REDIRECT_URI)}&scope=${scope}&state=${Math.random().toString(36)}`;

    // Open OAuth popup
    const width = 600;
    const height = 700;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);

    window.open(
      authUrl,
      'GitHub OAuth',
      `width=${width},height=${height},top=${top},left=${left}`
    );
  };

  // Create token manually (user provides it)
  const createTokenManually = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = await getVaultToken();
      if (!token) {
        throw new Error('Failed to authenticate with vault');
      }

      // Store token in vault
      const response = await fetch(`${VAULT_API}/api/v1/secrets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_name: 'shared',
          secrets: {
            [newToken.name || 'GITHUB_TOKEN']: newToken.value
          },
          created_by: 'admin-dashboard'
        })
      });

      if (response.ok) {
        setSuccess('GitHub token created successfully!');
        setShowCreateToken(false);
        setNewToken({ name: '', scopes: ['repo'], description: '', expirationDays: 90 });
        await loadTokens();

        // Verify the token
        await verifyGitHubToken(newToken.value);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create token');
      }
    } catch (err) {
      console.error('Failed to create token:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update existing token
  const updateToken = async (tokenName, newValue) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = await getVaultToken();
      if (!token) {
        throw new Error('Failed to authenticate with vault');
      }

      const response = await fetch(`${VAULT_API}/api/v1/secrets/shared/${tokenName}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          secret_value: newValue
        })
      });

      if (response.ok) {
        setSuccess('Token updated successfully!');
        await loadTokens();
        await verifyGitHubToken(newValue);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update token');
      }
    } catch (err) {
      console.error('Failed to update token:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete token
  const deleteToken = async (tokenName) => {
    if (!window.confirm(`Are you sure you want to delete ${tokenName}?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = await getVaultToken();
      if (!token) {
        throw new Error('Failed to authenticate with vault');
      }

      const response = await fetch(`${VAULT_API}/api/v1/secrets/shared/${tokenName}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Token deleted successfully!');
        await loadTokens();
        setGithubUser(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete token');
      }
    } catch (err) {
      console.error('Failed to delete token:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Test GitHub connection
  const testConnection = async (tokenValue) => {
    setIsAuthenticating(true);
    setError(null);

    try {
      const response = await fetch('https://api.github.com/user', {
        headers: {
          'Authorization': `token ${tokenValue}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setSuccess(`Connected as ${userData.login} (${userData.name})`);
        setGithubUser(userData);
      } else {
        const errorData = await response.json();
        setError(`GitHub API error: ${errorData.message || response.statusText}`);
      }
    } catch (err) {
      setError(`Connection failed: ${err.message}`);
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">GitHub Token Manager</h2>
        <p className="text-gray-600">
          Manage GitHub Personal Access Tokens for microservices integration
        </p>
      </div>

      {/* Status Banner */}
      {!isOnline && (
        <Alert type="warning" className="mb-4">
          Secrets Vault is offline. Token management is unavailable.
        </Alert>
      )}

      {error && (
        <Alert type="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert type="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* GitHub User Info */}
      {githubUser && (
        <Card className="mb-6 bg-green-50 border-green-200">
          <div className="flex items-center gap-4">
            <img
              src={githubUser.avatar_url}
              alt={githubUser.login}
              className="w-16 h-16 rounded-full"
            />
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{githubUser.name || githubUser.login}</h3>
              <p className="text-gray-600">@{githubUser.login}</p>
              <p className="text-sm text-gray-500">{githubUser.email}</p>
            </div>
            <Badge color="green">Authenticated</Badge>
          </div>
        </Card>
      )}

      {/* Actions */}
      <div className="mb-6 flex gap-4">
        {!githubUser && (
          <>
            <Button onClick={() => setShowCreateToken(true)} disabled={!isOnline || loading}>
              <i className="fas fa-plus mr-2"></i>
              Add Token Manually
            </Button>

            <Button
              onClick={startGitHubOAuth}
              disabled={!isOnline || loading}
              variant="secondary"
            >
              <i className="fab fa-github mr-2"></i>
              Connect via OAuth
            </Button>
          </>
        )}

        {githubUser && (
          <Button
            onClick={startGitHubOAuth}
            disabled={!isOnline || loading}
            variant="secondary"
          >
            <i className="fas fa-sync mr-2"></i>
            Reconnect via OAuth
          </Button>
        )}

        <Button
          onClick={() => setShowOAuthSetup(true)}
          variant="outline"
        >
          <i className="fas fa-cog mr-2"></i>
          OAuth Setup
        </Button>

        <Button onClick={loadTokens} disabled={!isOnline || loading} variant="outline">
          <i className="fas fa-sync-alt mr-2"></i>
          Refresh
        </Button>
      </div>

      {/* Tokens Table */}
      {loading && <Loader text="Loading tokens..." />}

      {!loading && tokens.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <i className="fab fa-github text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold mb-2">No GitHub Tokens Found</h3>
            <p className="text-gray-600 mb-6">
              Add a GitHub Personal Access Token to enable repository access
            </p>
            <Button onClick={() => setShowCreateToken(true)}>
              <i className="fas fa-plus mr-2"></i>
              Add Your First Token
            </Button>
          </div>
        </Card>
      )}

      {!loading && tokens.length > 0 && (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Token Name</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((token) => (
                <TableRow key={token.secret_key}>
                  <TableCell>
                    <div className="font-medium">{token.secret_key}</div>
                    <div className="text-sm text-gray-500">
                      Created by {token.created_by}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {new Date(token.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(token.created_at).toLocaleTimeString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge color={token.is_expired ? 'red' : 'green'}>
                      {token.is_expired ? 'Expired' : 'Active'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const tokenValue = prompt('Enter new token value:');
                          if (tokenValue) {
                            updateToken(token.secret_key, tokenValue);
                          }
                        }}
                      >
                        <i className="fas fa-edit mr-1"></i>
                        Update
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteToken(token.secret_key)}
                      >
                        <i className="fas fa-trash mr-1"></i>
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create Token Modal */}
      <Modal
        isOpen={showCreateToken}
        onClose={() => setShowCreateToken(false)}
        title="Add GitHub Token Manually"
        size="lg"
      >
        <div className="space-y-4">
          <Alert type="info">
            <div className="mb-2">
              <strong>To create a GitHub Personal Access Token:</strong>
            </div>
            <ol className="list-decimal ml-4 space-y-1 text-sm">
              <li>Go to <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">GitHub Settings → Tokens</a></li>
              <li>Click "Generate new token (classic)"</li>
              <li>Give it a name and select scopes (e.g., repo, read:org)</li>
              <li>Generate and copy the token (starts with ghp_)</li>
              <li>Paste it below</li>
            </ol>
          </Alert>

          <Input
            label="Token Name"
            placeholder="GITHUB_TOKEN"
            value={newToken.name}
            onChange={(e) => setNewToken({ ...newToken, name: e.target.value })}
          />

          <Input
            label="Token Value"
            type="password"
            placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            value={newToken.value}
            onChange={(e) => setNewToken({ ...newToken, value: e.target.value })}
          />

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowCreateToken(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => testConnection(newToken.value)}
              disabled={!newToken.value || isAuthenticating}
              variant="secondary"
            >
              {isAuthenticating ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button
              onClick={createTokenManually}
              disabled={!newToken.value || loading}
            >
              {loading ? 'Creating...' : 'Save Token'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* OAuth Setup Modal */}
      <Modal
        isOpen={showOAuthSetup}
        onClose={() => setShowOAuthSetup(false)}
        title="GitHub OAuth Setup"
        size="lg"
      >
        <div className="space-y-4">
          <Alert type="info">
            <strong>Setting up GitHub OAuth allows automatic token management</strong>
          </Alert>

          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-semibold mb-2">Step 1: Create GitHub OAuth App</h4>
            <ol className="list-decimal ml-4 space-y-2 text-sm">
              <li>Go to <a href="https://github.com/settings/developers" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">GitHub Settings → Developer settings → OAuth Apps</a></li>
              <li>Click "New OAuth App"</li>
              <li>Fill in:
                <ul className="list-disc ml-6 mt-1">
                  <li><strong>Application name:</strong> Microservices Dashboard</li>
                  <li><strong>Homepage URL:</strong> {window.location.origin}</li>
                  <li><strong>Authorization callback URL:</strong> {GITHUB_REDIRECT_URI}</li>
                </ul>
              </li>
              <li>Generate a Client Secret</li>
            </ol>
          </div>

          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-semibold mb-2">Step 2: Configure Environment Variables</h4>
            <pre className="bg-white p-3 rounded border text-sm overflow-x-auto">
REACT_APP_GITHUB_CLIENT_ID=your_client_id_here{'\n'}
REACT_APP_GITHUB_CLIENT_SECRET=your_client_secret_here
            </pre>
            <p className="text-sm text-gray-600 mt-2">
              Add these to your <code>.env.local</code> file and restart the dashboard.
            </p>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setShowOAuthSetup(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GitHubTokenManager;
